/**
 * evotech-oauth-return.ts — Cửa sổ popup uỷ quyền Evotech quay về eCDP (?evotech_connected=1 | ?evotech_error=… [&evotech_popup=1]).
 *
 * Chạy TRƯỚC khi dựng React (main.tsx): popup hẹp (~520px) bị coi là màn điện thoại → trang Kết nối không dựng ("Chức năng dùng trên
 * máy tính") nên bước "báo về cửa sổ chính rồi tự đóng" trong trang không bao giờ chạy (lỗi 10/10/2026). Đi qua trang Evotech
 * (khác tên miền, có thể đặt COOP) trình duyệt còn có thể cắt `window.opener` → báo qua 3 đường: postMessage (nếu còn opener),
 * BroadcastChannel và sự kiện localStorage (cùng origin, không cần opener). Trang Kết nối ở cửa sổ chính nghe cả 3 (khử trùng theo id).
 */

export const EVOTECH_OAUTH_MESSAGE = 'evotech-oauth'
/** Tên cửa sổ popup (window.open) — cũng dùng nhận diện popup khi không còn opener. */
export const EVOTECH_OAUTH_WINDOW = 'evotech-oauth'
export const EVOTECH_OAUTH_CHANNEL = 'ecdp-evotech-oauth'
export const EVOTECH_OAUTH_STORAGE_KEY = 'ecdp:evotech-oauth-result'

export interface EvotechOAuthMessage {
  type: typeof EVOTECH_OAUTH_MESSAGE
  connected: boolean
  error: string | null
  /** Khử trùng khi cửa sổ chính nhận cùng kết quả qua nhiều đường. */
  id?: string
}

export interface OAuthReturn {
  connected: boolean
  error: string | null
  popup: boolean
}

/** Đọc kết quả uỷ quyền từ query; null nếu trang không phải trang kết quả. */
export function readOAuthReturn(search: string): OAuthReturn | null {
  const q = new URLSearchParams(search)
  const error = q.get('evotech_error')
  const connected = q.has('evotech_connected')
  if (!connected && !error) return null
  return { connected, error, popup: q.get('evotech_popup') === '1' }
}

function safeOpener(): Window | null {
  try {
    const o = window.opener as Window | null
    return o && o !== window ? o : null
  } catch {
    return null
  }
}

/** Gửi kết quả về cửa sổ eCDP đã mở popup qua mọi đường khả dụng. */
export function broadcastOAuthResult(r: Pick<OAuthReturn, 'connected' | 'error'>): void {
  const msg: EvotechOAuthMessage = { type: EVOTECH_OAUTH_MESSAGE, connected: r.connected, error: r.error, id: `${Date.now()}-${Math.random().toString(36).slice(2)}` }
  try {
    safeOpener()?.postMessage(msg, window.location.origin)
  } catch { /* opener khác origin / đã đóng */ }
  try {
    const bc = new BroadcastChannel(EVOTECH_OAUTH_CHANNEL)
    bc.postMessage(msg)
    bc.close()
  } catch { /* trình duyệt không hỗ trợ */ }
  try {
    localStorage.setItem(EVOTECH_OAUTH_STORAGE_KEY, JSON.stringify(msg))
  } catch { /* bộ nhớ bị chặn */ }
}

/**
 * Nếu đây là popup uỷ quyền vừa quay về: báo kết quả, tự đóng, trả true (main.tsx KHÔNG dựng app, chỉ hiện ô báo nhỏ phòng khi
 * trình duyệt không cho script đóng cửa sổ). Không phải popup (popup bị chặn → mở cả trang) → false, trang Kết nối tự hiện thông báo.
 */
export function handleOAuthPopupReturn(): boolean {
  const r = readOAuthReturn(window.location.search)
  if (!r) return false
  const isPopup = r.popup || !!safeOpener() || window.name === EVOTECH_OAUTH_WINDOW
  if (!isPopup) return false
  broadcastOAuthResult(r)
  window.close()
  renderClosingNotice(r)
  return true
}

function renderClosingNotice(r: OAuthReturn): void {
  const root = document.getElementById('root') ?? document.body
  const ok = r.connected && !r.error
  root.innerHTML = `
    <div style="min-height:100vh;display:flex;align-items:center;justify-content:center;padding:24px;font-family:system-ui,-apple-system,'Segoe UI',sans-serif;background:#f8fafc;color:#0f172a">
      <div style="max-width:360px;text-align:center">
        <div style="font-size:40px;line-height:1">${ok ? '✅' : '⚠️'}</div>
        <p style="margin:12px 0 4px;font-size:17px;font-weight:600">${ok ? 'Đã kết nối tài khoản Evotech' : 'Chưa kết nối được Evotech'}</p>
        <p style="margin:0 0 16px;font-size:14px;color:#64748b">Kết quả đã gửi về cửa sổ eCDP. Bạn có thể đóng cửa sổ này.</p>
        <button id="evo-oauth-close" type="button" style="border:0;border-radius:8px;padding:9px 18px;background:#0045ff;color:#fff;font-size:14px;cursor:pointer">Đóng cửa sổ</button>
      </div>
    </div>`
  document.getElementById('evo-oauth-close')?.addEventListener('click', () => window.close())
}

/**
 * Nghe kết quả ở cửa sổ chính (trang Kết nối): postMessage + BroadcastChannel + localStorage; mỗi kết quả xử lý một lần.
 * Trả hàm huỷ đăng ký.
 */
export function listenOAuthResult(onResult: (m: EvotechOAuthMessage) => void): () => void {
  const seen = new Set<string>()
  const handle = (m: unknown) => {
    const msg = m as EvotechOAuthMessage | null
    if (!msg || msg.type !== EVOTECH_OAUTH_MESSAGE) return
    const key = msg.id ?? `${msg.connected}:${msg.error}`
    if (seen.has(key)) return
    seen.add(key)
    onResult(msg)
  }
  const onMessage = (e: MessageEvent) => {
    if (e.origin === window.location.origin) handle(e.data)
  }
  const onStorage = (e: StorageEvent) => {
    if (e.key !== EVOTECH_OAUTH_STORAGE_KEY || !e.newValue) return
    try {
      handle(JSON.parse(e.newValue))
    } catch { /* bỏ qua */ }
  }
  window.addEventListener('message', onMessage)
  window.addEventListener('storage', onStorage)
  let bc: BroadcastChannel | null = null
  try {
    bc = new BroadcastChannel(EVOTECH_OAUTH_CHANNEL)
    bc.onmessage = (e) => handle(e.data)
  } catch { /* không hỗ trợ */ }
  return () => {
    window.removeEventListener('message', onMessage)
    window.removeEventListener('storage', onStorage)
    bc?.close()
  }
}
